/**
 * Dejavoo PCI payment terminal hardware whitelist.
 *
 * Populate from Dejavoo PoC → Debug → Refresh device identity before sandbox/prod.
 * Matchers are case-insensitive; at least one matcher field must be set per entry.
 *
 * @example
 * { manufacturer: 'dejavoo', modelIncludes: 'Q3' }
 * @example
 * { modelExact: 'Z11' }
 */
export const DEJAVOO_PAYMENT_TERMINAL_WHITELIST = [
  { manufacturer: 'kozen', modelExact: 'P1' },
  { manufacturer: 'kozen', modelExact: 'P3' },
  { manufacturer: 'kozen', modelExact: 'P5' },
  { manufacturer: 'kozen', modelExact: 'P8' },
  { manufacturer: 'kozen', modelExact: 'P12' },
  { manufacturer: 'kozen', modelExact: 'P17' },
  { manufacturer: 'kozen', modelExact: 'P18' },
]

/**
 * @typedef {object} DejavooTerminalWhitelistEntry
 * @property {string} [manufacturer]
 * @property {string} [modelExact]
 * @property {string} [modelIncludes]
 * @property {string} [nameIncludes]
 */

/**
 * @param {object|null|undefined} info Capacitor Device.getInfo() shape (subset)
 * @param {DejavooTerminalWhitelistEntry[]} [whitelist]
 * @returns {{ matched: boolean, entry: DejavooTerminalWhitelistEntry|null, reason: string }}
 */
export function matchDejavooPaymentTerminal (info, whitelist = DEJAVOO_PAYMENT_TERMINAL_WHITELIST) {
  if (!info || !Array.isArray(whitelist) || whitelist.length === 0) {
    return { matched: false, entry: null, reason: 'whitelist_empty' }
  }

  const manufacturer = normalizeDeviceToken(info.manufacturer)
  const model = normalizeDeviceToken(info.model)
  const name = normalizeDeviceToken(info.name)

  for (const entry of whitelist) {
    if (!entry || typeof entry !== 'object') {
      continue
    }
    if (!matchesWhitelistEntry(entry, { manufacturer, model, name })) {
      continue
    }
    return { matched: true, entry, reason: 'whitelist_match' }
  }

  return { matched: false, entry: null, reason: 'no_whitelist_match' }
}

/**
 * Build a suggested whitelist entry from device info for copy/paste into this file.
 *
 * @param {object|null|undefined} info
 */
export function suggestWhitelistEntry (info) {
  const manufacturer = String(info?.manufacturer || '').trim()
  const model = String(info?.model || '').trim()
  const name = String(info?.name || '').trim()

  /** @type {DejavooTerminalWhitelistEntry} */
  const entry = {}
  if (manufacturer) {
    entry.manufacturer = manufacturer
  }
  if (model) {
    entry.modelExact = model
  } else if (name) {
    entry.nameIncludes = name
  }
  return entry
}

function normalizeDeviceToken (value) {
  return String(value || '').trim().toLowerCase()
}

/**
 * @param {DejavooTerminalWhitelistEntry} entry
 * @param {{ manufacturer: string, model: string, name: string }} normalized
 */
function matchesWhitelistEntry (entry, normalized) {
  const hasMatcher = ['manufacturer', 'modelExact', 'modelIncludes', 'nameIncludes']
    .some((key) => typeof entry[key] === 'string' && entry[key].trim() !== '')
  if (!hasMatcher) {
    return false
  }

  if (entry.manufacturer?.trim()) {
    const want = normalizeDeviceToken(entry.manufacturer)
    if (!normalized.manufacturer || normalized.manufacturer !== want) {
      return false
    }
  }

  if (entry.modelExact?.trim()) {
    const want = normalizeDeviceToken(entry.modelExact)
    if (!normalized.model || normalized.model !== want) {
      return false
    }
  }

  if (entry.modelIncludes?.trim()) {
    const needle = normalizeDeviceToken(entry.modelIncludes)
    if (!normalized.model || !normalized.model.includes(needle)) {
      return false
    }
  }

  if (entry.nameIncludes?.trim()) {
    const needle = normalizeDeviceToken(entry.nameIncludes)
    if (!normalized.name || !normalized.name.includes(needle)) {
      return false
    }
  }

  return true
}
